import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import toast, { Toaster, ToastBar } from 'react-hot-toast';
import App from './App';
import { AuthProvider } from './contexts/AuthContext';
import { BrandingProvider } from './contexts/BrandingContext';
import './styles/tokens.css';
import './index.css';
// Initialize i18n before App renders so the first paint is in the
// user's locale. The module's side effects register namespaces and
// hook the language toggle into i18n.changeLanguage.
import './i18n';

// React Query client with optimized caching
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes - data considered fresh
      gcTime: 1000 * 60 * 10, // 10 minutes - keep unused data in cache
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0, // Don't retry failed mutations
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <BrandingProvider>
          <AuthProvider>
            <App />
          <Toaster
            position="top-right"
            gutter={8}
            toastOptions={{
              duration: 4000,
              className: 'snow-toast',
              style: {
                background: 'rgb(var(--surface-card))',
                color: 'rgb(var(--gray-900))',
                fontSize: '13px',
                fontWeight: 500,
                letterSpacing: '-0.005em',
                lineHeight: 1.4,
                padding: '10px 12px',
                borderRadius: '10px',
                border: '1px solid var(--hairline-strong)',
                boxShadow:
                  '0 1px 2px rgba(0,0,0,0.12), 0 8px 24px rgba(0,0,0,0.14), inset 0 1px 0 0 rgba(255,255,255,0.04)',
                maxWidth: '380px',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#10b981',
                  secondary: 'rgb(var(--surface-card))',
                },
              },
              error: {
                duration: 5000,
                iconTheme: {
                  primary: '#ef4444',
                  secondary: 'rgb(var(--surface-card))',
                },
              },
              loading: {
                iconTheme: {
                  primary: 'var(--brand-primary)',
                  secondary: 'rgb(var(--surface-card))',
                },
              },
            }}
          >
            {(t) => (
              <ToastBar toast={t}>
                {({ icon, message }) => (
                  <>
                    {icon}
                    {message}
                    {t.type !== 'loading' && (
                      <button
                        onClick={() => toast.dismiss(t.id)}
                        aria-label="Close notification"
                        style={{
                          marginLeft: '4px',
                          width: '20px',
                          height: '20px',
                          background: 'transparent',
                          border: 'none',
                          color: 'rgb(var(--gray-500))',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '5px',
                          transition: 'background 0.15s ease, color 0.15s ease',
                          flexShrink: 0,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = 'rgb(var(--gray-900))'
                          e.currentTarget.style.background = 'rgba(127, 127, 127, 0.12)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = 'rgb(var(--gray-500))'
                          e.currentTarget.style.background = 'transparent'
                        }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    )}
                  </>
                )}
              </ToastBar>
            )}
          </Toaster>
          </AuthProvider>
        </BrandingProvider>
      </QueryClientProvider>
    </BrowserRouter>
  </React.StrictMode>
);
